import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Trash01 } from '@untitled-ui/icons-react'
import { MyColumn, MyDataTable } from '@interstellar-component'
import TruncatedText from '@src/components/TruncatedText'
import { enquiryStatusLabel } from '@src/services/enquiryStatus'
import ENQUIRY_CATEGORY_LABEL from '@src/services/enquiryCategory'
import paginatorToMeta from '@src/services/pagination'
import MySLAStatusChip from '@pages/ReportEnquiry/components/MySLAStatusChip'
import StatusChip from './StatusChip'
import LoadError from './LoadError'
import FilePreview from './FilePreview'
import { ROW_STATUS, chipOf, errorsWithTyped, outcomeNotes, rowField, rowFileChips } from '../constants'

// Errors or warnings of one row ([{ field, message }], written by the backend
// with the column name in the message): the first one, the rest on demand. An
// invalid row's error also shows what HR typed in that column (`typed`): the
// message itself masks long digit runs, so a rejected NIK is only visible here.
function MessagesCell({ items, tone }) {
  const [expanded, setExpanded] = useState(false)
  if (!items?.length) return <span className="text-sm text-gray-400">—</span>
  const shown = expanded ? items : items.slice(0, 1)
  const color = tone === 'error' ? 'text-error/700' : 'text-warning/700'
  return (
    <div className="flex w-[280px] flex-col gap-1 py-1">
      <ul className={`flex flex-col gap-1 text-xs ${color}`}>
        {shown.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="flex flex-col gap-0.5">
            <span className={expanded ? '' : 'line-clamp-2'}>{item.message}</span>
            {item.typed && (
              <span className="break-all text-gray-600">
                Typed: <span className="font-mono text-gray-800">{item.typed}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
      {items.length > 1 || (items[0]?.message || '').length > 90 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-max text-xs font-semibold text-brand/700 hover:underline"
        >
          {expanded ? 'Show less' : items.length > 1 ? `Show all ${items.length}` : 'Show more'}
        </button>
      ) : null}
    </div>
  )
}

MessagesCell.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ field: PropTypes.string, message: PropTypes.string, typed: PropTypes.string })
  ),
  tone: PropTypes.oneOf(['error', 'warning']).isRequired,
}

// One row field (rowField): an invalid row shows what HR typed, in red when
// its column is the one rejected.
function FieldText({ field, className, color }) {
  if (!field.text) return <span className={`${className} text-gray-400`}>—</span>
  return <TruncatedText text={field.text} className={`${className} ${field.invalid ? 'text-error/700' : color}`} />
}

FieldText.propTypes = {
  field: PropTypes.shape({ text: PropTypes.string, invalid: PropTypes.bool }).isRequired,
  className: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
}

// A row's files: a chip each, opening the same preview as the file grid (a
// photo in an <img>, a document as a PDF, a Word file offered as a download).
function FilesCell({ row, onPreview }) {
  const chips = rowFileChips(row.files)
  if (!chips.length) return <span className="text-sm text-gray-400">—</span>
  return (
    <div className="flex flex-col gap-1 py-1">
      {chips.map((chip) =>
        chip.url ? (
          <button
            key={chip.kind}
            type="button"
            title="Preview"
            aria-label={`Preview ${chip.label} of row ${row.row_no}`}
            onClick={() => onPreview({ kind: chip.kind, saved: row.files[chip.kind], row })}
          >
            <StatusChip label={chip.label} color={chip.color} />
          </button>
        ) : (
          <StatusChip key={chip.kind} label={chip.label} color={chip.color} />
        )
      )}
    </div>
  )
}

FilesCell.propTypes = { row: PropTypes.object.isRequired, onPreview: PropTypes.func.isRequired }

// What happened after a created row's enquiry was made (outcome_error): most
// are notes (on hold and funded automatically, sent to CLIK a minute later),
// only a withdrawn enquiry is an error. The backend's reason on a second line.
function OutcomeNotes({ outcomeError }) {
  const notes = outcomeNotes(outcomeError)
  if (!notes.length) return null
  return (
    <ul className="flex flex-col gap-1">
      {notes.map((note, i) => (
        <li key={i} className="flex flex-col gap-0.5 text-xs">
          <span className={note.tone === 'error' ? 'text-error/700' : 'text-gray-700'}>{note.label}</span>
          {note.detail && (
            <span className="line-clamp-2 text-gray-500" title={note.detail}>
              {note.detail}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

OutcomeNotes.propTypes = { outcomeError: PropTypes.string }

// The enquiry a created row became. Not a link yet: its page is the employee
// sheet (/workforce/employee/:workforceId?enquiry=:id) and the rows API does
// not return the workforce id.
function EnquiryCell({ enquiry }) {
  if (!enquiry) return <span className="text-sm text-gray-400">—</span>
  if (enquiry.removed) return <span className="text-xs text-gray-500">Deleted from Report Enquiry</span>
  if (enquiry.superseded) return <span className="text-xs text-gray-500">Replaced by a later request</span>
  return (
    <div className="flex flex-col gap-1 py-1">
      <span className="text-sm font-medium text-gray-900">{enquiry.reference_number || '—'}</span>
      <MySLAStatusChip status={enquiryStatusLabel(enquiry)} />
    </div>
  )
}

EnquiryCell.propTypes = { enquiry: PropTypes.object }

/**
 * Staged rows of one batch, read-only except "remove row" (open batch, row not
 * yet an enquiry). Everything shown is the rows API as returned. hrKtp: the
 * batch takes the e-KTP from HR (needs_hr_ktp); otherwise a NIK typed in an
 * invalid row was ignored and is not shown.
 */
function BatchRowsTable({ rows, hrKtp, canRemove, onRemove, onChangePage }) {
  const [preview, setPreview] = useState(null) // { kind, saved, row }
  if (rows.error) {
    return <LoadError title="Could not load the rows" message={rows.error.message} onRetry={rows.reload} />
  }

  const values = {
    data: rows.data,
    // The table is read again after every write and every run: the rows
    // shown stay until the new ones arrive (a spinner only when there are none).
    loading: rows.loading && !rows.data.length,
    meta: paginatorToMeta(rows.paginator),
    checkedAll: false,
  }

  return (
    <>
      <FilePreview
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        kind={preview?.kind}
        saved={preview?.saved}
        row={preview?.row}
      />
      <MyDataTable values={values} paginator onChangePagination={onChangePage}>
        <MyColumn
          header="Row"
          field="row_no"
          alignment="center"
          body={(row) => <span className="text-sm text-gray-500">{row.row_no}</span>}
        />
        <MyColumn
          header="Name & email"
          field="full_name"
          body={(row) => {
            const employeeCode = rowField(row, 'employee_code')
            return (
              <div className="flex max-w-[240px] min-w-0 flex-col gap-0.5 py-1">
                <FieldText field={rowField(row, 'full_name')} className="text-sm font-medium" color="text-gray-900" />
                <FieldText field={rowField(row, 'email')} className="text-xs" color="text-gray-500" />
                {employeeCode.text && (
                  <span className={`text-xs ${employeeCode.invalid ? 'text-error/700' : 'text-gray-500'}`}>
                    ID {employeeCode.text}
                  </span>
                )}
              </div>
            )
          }}
        />
        <MyColumn
          header="Category"
          field="category"
          body={(row) => {
            const category = rowField(row, 'category')
            return (
              <span className={`text-sm ${category.invalid ? 'text-error/700' : 'text-gray-700'}`}>
                {ENQUIRY_CATEGORY_LABEL[category.text] || category.text || '—'}
              </span>
            )
          }}
        />
        <MyColumn
          header="NIK"
          field="nik"
          body={(row) => {
            const nik = rowField(row, 'nik', { typed: hrKtp })
            return (
              <span className={`font-mono text-xs ${nik.invalid ? 'text-error/700' : 'text-gray-700'}`}>
                {nik.text || '—'}
              </span>
            )
          }}
        />
        <MyColumn
          header="Status"
          field="status"
          body={(row) => (
            <div className="flex max-w-[220px] flex-col gap-1 py-1">
              <StatusChip {...chipOf(ROW_STATUS, row.status)} dot />
              <OutcomeNotes outcomeError={row.outcome_error} />
            </div>
          )}
        />
        <MyColumn header="Files" field="files" body={(row) => <FilesCell row={row} onPreview={setPreview} />} />
        <MyColumn
          header="Errors"
          field="errors"
          body={(row) => <MessagesCell items={errorsWithTyped(row)} tone="error" />}
        />
        <MyColumn
          header="Warnings"
          field="warnings"
          body={(row) => <MessagesCell items={row.warnings} tone="warning" />}
        />
        <MyColumn header="Enquiry" field="enquiry" body={(row) => <EnquiryCell enquiry={row.enquiry} />} />
        {canRemove && (
          <MyColumn
            header=""
            field="action"
            alignment="right"
            body={(row) =>
              row.status === 'created' ? null : (
                <button
                  type="button"
                  title="Remove row"
                  aria-label={`Remove row ${row.row_no}`}
                  onClick={() => onRemove(row)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-error/50 hover:text-error/700"
                >
                  <Trash01 className="size-4" stroke="currentColor" />
                </button>
              )
            }
          />
        )}
      </MyDataTable>
    </>
  )
}

BatchRowsTable.propTypes = {
  rows: PropTypes.shape({
    data: PropTypes.array,
    paginator: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.object,
    reload: PropTypes.func,
  }).isRequired,
  hrKtp: PropTypes.bool.isRequired,
  canRemove: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
}

// A Process run re-renders the batch page on every event: the table only
// renders again when its rows (or its other props) change.
export default React.memo(BatchRowsTable)
